/**
 * ComposeModal — 写邮件对话框（QQ 风格行内布局 + 富文本工具栏 + 抄送/密送/分别发送）
 * 复用后端 POST /api/v1/compose/send（账号级发信，校验本地邮箱身份）。
 * 支持从邮件详情预填：回复（to=原from, subject=Re:）/ 转发（subject=Fwd:）。
 *
 * 富文本：编辑器为 contentEditable 区域，工具栏提供 加粗/斜体/下划线/图片。
 * 图片以 base64 dataURL 内联写入 body_html（后端已支持 body_html，无需附件）。
 * 发送时取编辑器 innerHTML 作为用户新写正文，再合并「原始邮件」只读引用块，
 * 最终 body_html/body_text 一并发出（保留 QQ 回复引用样式）。
 *
 * 注意：「原始邮件」以独立只读 div 渲染在编辑器**下方**，**不**放进 contentEditable，
 * 因此不可编辑、文本光标也进不去——这是为了符合截图里 QQ 的灰底引用块交互。
 */
import { useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Input,
  Button,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  DialogTitle,
  DialogActions,
  Divider,
} from '@fluentui/react-components'
import {
  SendRegular,
  TextBold16Regular,
  TextItalic16Regular,
  TextUnderline16Regular,
  Image16Regular,
} from '@fluentui/react-icons'
import { composeApi } from '@/api/compose'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'
import { formatDate } from '@/lib/format'

const useStyles = makeStyles({
  surface: { width: '720px', maxWidth: '92vw' },
  title: { fontSize: '14px', fontWeight: 500 },
  container: { display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '2px' },
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minHeight: '32px',
  },
  label: {
    width: '64px',
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
    fontSize: '13px',
    textAlign: 'right',
  },
  input: { flexGrow: 1, minWidth: 0 },
  // 抄送/密送/分别发送 切换行
  toggles: { display: 'flex', gap: '8px', paddingLeft: '76px', flexWrap: 'wrap' },
  toggleBtn: { minWidth: 'auto', padding: '2px 10px', height: '24px', fontSize: '12px' },
  divider: { marginTop: '4px', marginBottom: '4px' },
  // 富文本工具栏
  toolbar: {
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
    padding: '4px 0',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  toolBtn: { minWidth: 'auto', padding: '2px 8px', height: '28px' },
  // 正文（contentEditable 富文本）
  editor: {
    minHeight: '200px',
    maxHeight: '46vh',
    overflowY: 'auto',
    padding: '10px 12px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '4px',
    fontFamily: 'inherit',
    fontSize: '13px',
    lineHeight: '1.6',
    color: tokens.colorNeutralForeground1,
    outline: 'none',
  },
  // 原始邮件只读块（QQ 风格灰底引用）—— 独立 div，不放入编辑器
  quoteHeading: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground4,
    textAlign: 'center',
    padding: '12px 0 4px',
    userSelect: 'none',
  },
  quoteBox: {
    marginTop: '4px',
    padding: '8px 12px',
    borderLeft: '3px solid #cfcfcf',
    background: '#f5f5f5',
    borderRadius: '2px',
    color: '#888',
    fontSize: '13px',
    lineHeight: '1.6',
    userSelect: 'text',
    cursor: 'default',
  },
  quoteMeta: { marginBottom: '6px' },
  quoteBody: { whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '8px' },
})

/** 回复/转发时引用的原邮件元信息 */
export interface OriginalEmail {
  from: string
  to: string
  /** ISO 字符串 */
  date: string
  subject: string
  bodyText: string
}

export interface ComposeModalProps {
  open: boolean
  onClose: () => void
  /** 默认发件人（通常为当前查看的邮箱账号） */
  defaultFrom: string
  defaultTo?: string
  defaultSubject?: string
  defaultBody?: string
  /** 标题：写邮件 / 回复邮件 / 转发邮件 */
  title?: string
  /** 回复/转发时传入；渲染为只读引用块，**不**放入可编辑区域 */
  originalEmail?: OriginalEmail
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/** 把原邮件渲染为只读引用块的 HTML（视觉用），以及对应的纯文本引用块（发送用） */
function buildQuotedHtml(orig: OriginalEmail): string {
  const body = escapeHtml(orig.bodyText || '（无正文）')
  const date = formatDate(orig.date)
  return (
    `<div style="margin:12px 0;padding:8px 12px;border-left:3px solid #cfcfcf;` +
    `background:#f5f5f5;color:#888;">` +
    `<div style="margin-bottom:6px;">在 ${date}，${escapeHtml(orig.from || '发件人')} 写道：</div>` +
    `<div style="white-space:pre-wrap;">${body}</div>` +
    `</div>`
  )
}

function buildQuotedText(orig: OriginalEmail): string {
  const date = formatDate(orig.date)
  const from = orig.from || '发件人'
  const body = orig.bodyText || '（无正文）'
  return `在 ${date}，${from} 写道：\n${body
    .split('\n')
    .map((l) => `> ${l}`)
    .join('\n')}`
}

/** 把 HTML 转成纯文本，作为 body_text 兜底 */
function htmlToText(html: string): string {
  const div = document.createElement('div')
  div.innerHTML = html
  return (div.textContent || '').replace(/ /g, ' ').trim()
}

/** 把光标定位到给定 contentEditable 元素的最前面 */
function placeCaretAtStart(el: HTMLElement) {
  el.focus()
  const range = document.createRange()
  range.setStart(el, 0)
  range.collapse(true)
  const sel = window.getSelection()
  if (sel) {
    sel.removeAllRanges()
    sel.addRange(range)
  }
}

function splitAddrs(s: string): string[] {
  return s
    .split(/[,;]/)
    .map((x) => x.trim())
    .filter(Boolean)
}

export default function ComposeModal({
  open,
  onClose,
  defaultFrom,
  defaultTo = '',
  defaultSubject = '',
  defaultBody = '',
  title = '写邮件',
  originalEmail,
}: ComposeModalProps) {
  const styles = useStyles()
  const editorRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [from, setFrom] = useState(defaultFrom)
  const [to, setTo] = useState(defaultTo)
  const [cc, setCc] = useState('')
  const [bcc, setBcc] = useState('')
  const [subject, setSubject] = useState(defaultSubject)
  const [showCc, setShowCc] = useState(false)
  const [showBcc, setShowBcc] = useState(false)
  const [sendSeparately, setSendSeparately] = useState(false)

  // 打开时初始化：重置字段 + 编辑器只放 defaultBody（不含原始邮件，保持只读块独立），焦点落在编辑器顶部
  useEffect(() => {
    if (open) {
      setFrom(defaultFrom)
      setTo(defaultTo)
      setCc('')
      setBcc('')
      setSubject(defaultSubject)
      setShowCc(false)
      setShowBcc(false)
      setSendSeparately(false)
      if (editorRef.current) {
        editorRef.current.innerHTML = defaultBody || ''
        // 异步到下一帧，等 dialog 真正挂载、把焦点顶到编辑器最前面
        requestAnimationFrame(() => {
          if (editorRef.current && document.body.contains(editorRef.current)) {
            placeCaretAtStart(editorRef.current)
          }
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultFrom, defaultTo, defaultSubject, defaultBody])

  const exec = (cmd: string) => {
    editorRef.current?.focus()
    document.execCommand(cmd, false)
  }

  const onPickImage = () => fileInputRef.current?.click()

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = '' // 允许重复选同一文件
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      editorRef.current?.focus()
      document.execCommand('insertImage', false, reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  const toggleCc = () => {
    setShowCc((v) => {
      const nv = !v
      if (!nv) setCc('')
      return nv
    })
  }
  const toggleBcc = () => {
    setShowBcc((v) => {
      const nv = !v
      if (!nv) setBcc('')
      return nv
    })
  }

  const sendMutation = useMutation({
    mutationFn: () => {
      const userHtml = editorRef.current?.innerHTML ?? ''
      const userText = htmlToText(userHtml)
      // 把用户新写内容与「原始邮件」引用块合并后发出，保证 QQ 风格的引用样式
      const quotedHtml = originalEmail ? buildQuotedHtml(originalEmail) : ''
      const quotedText = originalEmail ? buildQuotedText(originalEmail) : ''
      const bodyHtml = quotedHtml
        ? userHtml.trim()
          ? `${userHtml}${quotedHtml}`
          : quotedHtml
        : userHtml.trim()
      const bodyText = quotedText
        ? userText
          ? `${userText}\n\n${quotedText}`
          : quotedText
        : userText
      return composeApi.send({
        from: from.trim(),
        recipients: splitAddrs(to),
        cc: splitAddrs(cc),
        bcc: splitAddrs(bcc),
        subject: subject.trim(),
        body_html: bodyHtml,
        body_text: bodyText,
        send_separately: sendSeparately,
      })
    },
    onSuccess: (res) => {
      if (res.failed > 0) {
        toast.warning('部分发送失败', `${res.sent}/${res.total} 成功，${res.failed} 失败`)
      } else {
        toast.success('已发送', `成功发送给 ${res.sent} 个收件人`)
      }
      onClose()
    },
    onError: (err) => toast.error('发送失败', err instanceof ApiError ? err.message : '请检查输入'),
  })

  const canSend = !!from.trim() && (splitAddrs(to).length > 0 || splitAddrs(cc).length > 0 || splitAddrs(bcc).length > 0)

  if (!open) return null

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()} modalType="modal">
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle className={styles.title}>{title}</DialogTitle>
          <DialogContent>
            <div className={styles.container}>
              {/* 发件人 */}
              <div className={styles.fieldRow}>
                <span className={styles.label}>发件人</span>
                <Input
                  className={styles.input}
                  value={from}
                  onChange={(_, d) => setFrom(d.value)}
                  placeholder="you@yourdomain.com"
                />
              </div>
              {/* 收件人 */}
              <div className={styles.fieldRow}>
                <span className={styles.label}>收件人</span>
                <Input
                  className={styles.input}
                  value={to}
                  onChange={(_, d) => setTo(d.value)}
                  placeholder="多个收件人用逗号或分号分隔"
                />
              </div>
              {/* 抄送（可显隐） */}
              {showCc && (
                <div className={styles.fieldRow}>
                  <span className={styles.label}>抄送</span>
                  <Input
                    className={styles.input}
                    value={cc}
                    onChange={(_, d) => setCc(d.value)}
                    placeholder="抄送收件人，用逗号或分号分隔"
                  />
                </div>
              )}
              {/* 密送（可显隐） */}
              {showBcc && (
                <div className={styles.fieldRow}>
                  <span className={styles.label}>密送</span>
                  <Input
                    className={styles.input}
                    value={bcc}
                    onChange={(_, d) => setBcc(d.value)}
                    placeholder="密送收件人，用逗号或分号分隔"
                  />
                </div>
              )}
              {/* 抄送 / 密送 / 分别发送 切换 */}
              <div className={styles.toggles}>
                <Button
                  className={styles.toggleBtn}
                  appearance={showCc ? 'primary' : 'subtle'}
                  onClick={toggleCc}
                >
                  抄送
                </Button>
                <Button
                  className={styles.toggleBtn}
                  appearance={showBcc ? 'primary' : 'subtle'}
                  onClick={toggleBcc}
                >
                  密送
                </Button>
                <Button
                  className={styles.toggleBtn}
                  appearance={sendSeparately ? 'primary' : 'subtle'}
                  onClick={() => setSendSeparately((v) => !v)}
                  title="开启后每位收件人各收到独立一封（互不可见）"
                >
                  分别发送
                </Button>
              </div>
              {/* 主 题 */}
              <div className={styles.fieldRow}>
                <span className={styles.label}>主 题</span>
                <Input
                  className={styles.input}
                  value={subject}
                  onChange={(_, d) => setSubject(d.value)}
                />
              </div>

              <Divider className={styles.divider} />

              {/* 富文本工具栏 */}
              <div className={styles.toolbar}>
                <Button className={styles.toolBtn} appearance="subtle" icon={<TextBold16Regular />} onClick={() => exec('bold')} title="加粗" />
                <Button className={styles.toolBtn} appearance="subtle" icon={<TextItalic16Regular />} onClick={() => exec('italic')} title="斜体" />
                <Button className={styles.toolBtn} appearance="subtle" icon={<TextUnderline16Regular />} onClick={() => exec('underline')} title="下划线" />
                <Button className={styles.toolBtn} appearance="subtle" icon={<Image16Regular />} onClick={onPickImage} title="插入图片" />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={onFileChange}
                />
              </div>

              {/* 正文（contentEditable 富文本 —— 仅承载用户新写内容，不放原始邮件） */}
              <div
                ref={editorRef}
                className={styles.editor}
                contentEditable
                suppressContentEditableWarning
                data-placeholder="正文"
              />

              {/* 「原始邮件」只读引用块 —— 独立 div，不放入编辑器，因此不可编辑、不会获取焦点 */}
              {originalEmail && (
                <>
                  <div className={styles.quoteHeading}>—— 原始邮件 ——</div>
                  <div className={styles.quoteBox}>
                    <div className={styles.quoteMeta}>
                      发件人：{originalEmail.from || '（未知）'}
                      {'\n'}
                      发件时间：{formatDate(originalEmail.date)}
                      {'\n'}
                      收件人：{originalEmail.to || '（未知）'}
                      {'\n'}
                      主 题：{originalEmail.subject || '（无主题）'}
                    </div>
                    <div className={styles.quoteBody}>{originalEmail.bodyText || '（无正文）'}</div>
                  </div>
                </>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="subtle" onClick={onClose}>
              取消
            </Button>
            <Button
              appearance="primary"
              icon={<SendRegular />}
              onClick={() => sendMutation.mutate()}
              disabled={sendMutation.isPending || !canSend}
            >
              {sendMutation.isPending ? '发送中…' : '发送'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
